import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { Glyph, PHONE } from '~/components/decision';
import { Screen } from '~/components/Screen';
import { STAGE_LABEL, StageRow, stageState } from '~/components/stages';
import { Card, Eyebrow, OutlineButton, PrimaryButton } from '~/components/ui';
import type { JobStatus } from '~/lib/api';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * The real API statuses, in the order they occur, each with the note it carries here.
 *
 * The fifth is not a machine stage: since D19 most jobs go through a final check before
 * the verdict is released. Without a picture for it the app reads as hung, because the
 * four machine stages take a minute and this one can take hours.
 *
 * It is called "Processing", not what it is. The field app does not name the internal
 * step — an operator standing at a customer's car who is told a person is reviewing it
 * gets asked who, and how long, and can answer neither.
 *
 * The LABEL comes from `STAGE_LABEL`, shared with the job timeline. The notes are local
 * and are the code's own: they say what the stage is doing, and that is one of the
 * field-tested strings the re-skin does not overwrite.
 */
const STAGES: Array<{ status: JobStatus; note: string }> = [
  { status: 'queued', note: 'Accepted' },
  { status: 'ingesting', note: 'Photos received' },
  { status: 'analysing', note: 'Each photograph read independently, then compared' },
  { status: 'enriching', note: 'VIN decode + glass type' },
  {
    status: 'pending_review',
    note: 'Final checks on the photographs and the assessment. This can take a while.',
  },
];

/** "0 m 04 s" — the prototype's format, and it reads at a glance where 00:04 does not. */
function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)} m ${String(seconds % 60).padStart(2, '0')} s`;
}

export interface WaitingScreenProps {
  reference: string;
  areaCount: number;
  status: JobStatus;
  onLeave: () => void;
}

/**
 * The wait, with a real narrative instead of a spinner.
 *
 * The stages are the API's own status values, not invented steps — so the highlighted
 * one is genuinely where the job is. Elapsed time is counted locally because the API
 * exposes no per-stage timestamps: there is a single `status` column and an `updated_at`
 * heartbeated at every leg boundary, so a real per-stage breakdown is not available to
 * show even if we wanted to.
 */
export function WaitingScreen({ reference, areaCount, status, onLeave }: WaitingScreenProps) {
  const { c, t, space } = useTheme();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const currentIndex = STAGES.findIndex((s) => s.status === status);
  const held = status === 'pending_review';

  /*
    Four stages while a machine has it, five once a person does.

    The review stage is NOT drawn greyed in advance. Every job is held (insufficient_evidence
    too, since Appendix H 2026-09-29) except an exempt caller's, or all of them under
    REVIEW_MODE=off — so promising a fifth stage to every job would be a promise the
    pipeline does not make. It appears when it is true.
  */
  const visible = held ? STAGES : STAGES.slice(0, 4);

  return (
    <Screen>
      <View style={{ flex: 1, padding: space.md, gap: space.md }}>
        <View style={{ gap: space.xxs }}>
          <Eyebrow>{`Sent · ${reference} · ${areaCount} area${areaCount === 1 ? '' : 's'}`}</Eyebrow>
          <Text style={[t.h1, { color: c.text }]}>
            {held ? 'Processing' : 'Assessing the glass'}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'baseline',
              gap: space.sm,
              paddingTop: space.xs,
            }}
          >
            <Text style={[t.monoValue, { color: c.text }]} testID="elapsed">
              {clock(elapsed)}
            </Text>
            <Text style={[t.body, { color: c.dim }]}>elapsed</Text>
          </View>
          <Text style={[t.body, { color: c.dim }]}>Your assessment is being processed.</Text>
        </View>

        <Card>
          {visible.map((stage, i) => {
            const state = stageState(i, currentIndex, stage.status === 'pending_review');
            return (
              <StageRow
                key={stage.status}
                state={state}
                label={STAGE_LABEL[stage.status] ?? stage.status}
                // Only the stage the job is ON carries its note. A note under every stage
                // that has happened is a paragraph to read past to find the live one.
                note={i === currentIndex ? stage.note : undefined}
                last={i === visible.length - 1}
              />
            );
          })}
        </Card>
      </View>

      <View style={{ paddingHorizontal: space.md, paddingBottom: space.md, gap: space.md }}>
        {/* Reassurance, not friction: a journey costs real money, so the operator needs to
            know that leaving does not cancel it or bill a second one. */}
        <View style={{ flexDirection: 'row', gap: space.sm, alignItems: 'flex-start' }}>
          {/*
            The PHONE glyph either way. A person icon said "someone is looking at this" as
            plainly as the words did, and the field app does not name the internal step.
          */}
          <Glyph path={PHONE} size={20} color={held ? c.stateInfo : c.text} />
          {held ? (
            <View style={{ flex: 1, gap: space.xxs }}>
              <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>
                Leave this screen.
              </Text>
              <Text style={[t.body, { color: c.dim }]}>
                The verdict lands in Recent journeys when it is ready, whether or not the app is
                open.
              </Text>
            </View>
          ) : (
            <Text style={[t.body, { flex: 1, color: c.dim }]}>
              You can put the phone down. The verdict lands in Recent journeys, whether or not the
              app is open.
            </Text>
          )}
        </View>
        {held ? (
          <PrimaryButton testID="leave-waiting" label="Back to recent journeys" onPress={onLeave} />
        ) : (
          <OutlineButton testID="leave-waiting" label="Back to recent journeys" onPress={onLeave} />
        )}
      </View>
    </Screen>
  );
}
